import { useCallback, useState } from "react";
import { usePage } from "@inertiajs/react";
import { router } from "@inertiajs/react";
import { useDeployService } from "@api/deploys.hooks";
import { useDeployType } from "@hooks/useDeployInstructions";

/**
 * Start a fresh deploy directly.
 * Used by both the "Redeploy" button (complete screen) and "Retry Deploy" (error screen).
 * POSTs to /api/v1/deploys to create a new pending deploy (which deactivates
 * the old one and enqueues the orchestrator), then reloads Inertia props so
 * the deploy page re-renders with the freshly-created current_deploy.
 *
 * Campaign-type deploys must include the campaign id — projects can have
 * multiple campaigns, and the orchestrator needs to know which one to deploy.
 * The id comes from Inertia::CampaignDeployProps's `campaign.id`. (UUIDs
 * stay in user-visible URL paths; request bodies use integer ids.)
 */
export function useRedeploy() {
  const service = useDeployService();
  const props = usePage<{
    project?: { id: number };
    campaign?: { id: number };
  }>().props;
  const deployType = useDeployType();
  const [isLoading, setIsLoading] = useState(false);

  const trigger = useCallback(async () => {
    const projectId = props.project?.id;
    if (!projectId) return;

    const campaignId = props.campaign?.id;
    if (deployType === "campaign" && !campaignId) {
      // Defensive: this means we're on /campaigns/:uuid/deploy without
      // campaign in props, or someone wired this hook into a non-campaign
      // page that nonetheless reports deployType=campaign. Either is a
      // page-prop bug — fail loud rather than fire an API call we know
      // the backend will 422.
      console.error(
        "[useRedeploy] deployType=campaign but no campaign.id in page props — refusing to fire deploy"
      );
      return;
    }

    setIsLoading(true);
    try {
      const created = await service.create({ projectId, deployType, campaignId });
      // Carry the freshly-created deploy id into the reload's GET so the
      // controller renders THIS deploy explicitly. Eliminates the flash of
      // the previous (terminal/failed) deploy that `current_for`'s
      // historical fallback would otherwise return if the new active row
      // isn't yet visible to the next request.
      router.reload({ data: { deploy_id: created.id } });
    } catch {
      setIsLoading(false);
    }
  }, [props.project?.id, props.campaign?.id, service, deployType]);

  return { trigger, isLoading };
}
